<template>
  <div class="app-container">
    <!-- <Top></Top> -->

    <!-- 搜索结果头部 -->
    <section class="search-results-header">
      <div class="container">
        <h1>“{{ searchKey }}” 共 {{ totalCount }} 个搜索结果</h1>
      </div>
    </section>

    <!-- 核心模型展示区 -->
    <section class="models-section" id="models">
      <div class="container">
        <!-- 首次加载骨架屏 -->
        <div v-if="isLoading" class="skeleton-container">
          <div class="skeleton-card" v-for="i in 10" :key="i"></div>
        </div>

        <!-- 模型列表（懒加载显示） -->
        <div class="models-grid" v-else>
          <div class="model-card" v-for="model in displayedModels" :key="model.id" @click="handleModelDetail(model.id)">
            <div class="model-img-container" @click="goToDetail(model.id)">
              <img :src="model.img" :alt="model.coverName" class="model-img" />
              <div class="model-tag" v-if="model.isExclusive">独家发售</div>
              <!-- 新增：官方认证标签 -->
              <div class="model-official-tag" v-if="model.officialmod">官方认证</div>
              <!-- 新增：可开票标签 -->
              <div class="model-invoice-tag" v-if="model.whetherInvoice">可开发票</div>
            </div>
            <div class="model-info">
              <h3 class="model-name">{{ model.name }}</h3>
              <div class="model-meta">
                <span class="model-price">{{ model.price }}云币</span>
                <span class="model-sales">已售{{ model.sales }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 无数据提示 -->
        <div v-if="!isLoading && allModels.length === 0" class="no-data">暂无相关模型</div>

        <!-- 滚动加载中提示（仅首次加载后追加时显示） -->
        <div v-if="isAppendLoading" class="loading">加载中...</div>

        <!-- 无更多数据提示 -->
        <div v-if="!hasMore && displayedModels.length > 0" class="no-more">已加载全部模型</div>
      </div>
    </section>

    <!-- <Bottom></Bottom> -->
  </div>
</template>

<script>
import Top from '../../components/Header.vue';
import Bottom from '../../components/Footer.vue';

export default {
  name: 'SearchResults',
  components: {
    Top,
    Bottom
  },
  data() {
    return {
      activeTab: 'all',
      searchKey: '', // 搜索关键词（路由传递）
      allModels: [], // 存储全部模型数据（首次请求一次性加载）
      displayedModels: [], // 当前显示的模型数据（滚动时追加）
      totalCount: 0, // 总模型数量
      isLoading: true, // 首次加载状态（加载全部数据）
      isAppendLoading: false, // 滚动追加显示时的加载状态
      pageSize: 20, // 每次滚动追加的条数（懒加载批次大小）
      currentBatch: 1, // 当前显示的批次（第1批、第2批...）
      hasMore: true, // 是否还有更多数据可显示
      scrollDebounceTimer: null, // 滚动防抖计时器
      scrollHandler: null, // 滚动事件处理函数引用（新增）
      resizeHandler: null // 窗口变化事件处理函数引用（新增）
    };
  },
  computed: {
    // 筛选后的全部模型（按tab分类）
    filteredAllModels() {
      return this.activeTab === 'all'
        ? this.allModels
        : this.allModels.filter(model => model.category === this.activeTab);
    }
  },
  watch: {
    // 切换tab时，重新初始化懒加载显示
    activeTab() {
      this.currentBatch = 1;
      this.displayedModels = this.getBatchData(1); // 显示第1批
      // 修复：使用更准确的判断逻辑
      this.hasMore = this.filteredAllModels.length > this.displayedModels.length;
      //console.log('📌 切换Tab后，是否还有更多数据：', this.hasMore);
    }
  },
  mounted() {
    // 1. 强制解除页面滚动阻塞（新增）
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.body.style.height = 'auto';

    // 2. 从路由获取搜索关键词
    this.searchKey = this.$route.query.title || '';
    //console.log('📌 获取到搜索关键词（标题）：', this.searchKey);

    // 3. 首次加载全部模型数据
    this.fetchAllModels();

    // 4. 确保DOM就绪后绑定事件（新增：优化事件绑定）
    this.$nextTick(() => {
      // 绑定滚动事件
      this.scrollHandler = this.handleScrollLazyLoad.bind(this);
      window.addEventListener('scroll', this.scrollHandler);
      // 绑定窗口变化事件
      this.resizeHandler = this.handleResize.bind(this);
      window.addEventListener('resize', this.resizeHandler);

      //console.log('✅ 滚动事件和窗口变化事件已成功绑定');
    });
  },
  beforeDestroy() {
    // 修复：使用存储的函数引用移除事件（新增）
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    clearTimeout(this.scrollDebounceTimer);
    //console.log('✅ 滚动事件和窗口变化事件已成功移除');
  },
  methods: {
    // 窗口大小变化处理
    handleResize() {
      this.$nextTick(() => {
        // 触发重排，确保布局适配
        document.body.style.overflow = 'auto';
        //console.log('📌 窗口大小变化，已适配布局');
      });
    },

    // 接口数据映射到本地格式
    mapApiDataToLocal(apiItem) {
      return {
        id: apiItem.modelId, // 唯一ID（避免重复渲染）
        name: apiItem.title,
        coverName: apiItem.coverName[0],
        img: apiItem.coverUrl[0],
        price: apiItem.prices,
        sales: apiItem.salesVolume,
        category: apiItem.category || "all", // 补充分类字段，兼容筛选
        isExclusive: apiItem.isExclusive || false, // 补充独家标签字段
        officialmod: apiItem.officialmod || false, // 新增：官方认证
        whetherInvoice: apiItem.whetherInvoice || false // 新增：可开票
      };
    },

    // 首次加载全部模型数据（接口返回所有数据）
    fetchAllModels() {
      this.isLoading = true;
      //console.log('📌 开始加载全部模型数据（按标题搜索）');

      // 调用接口（无需分页参数，请求全部数据）
      this.$apiFun.getSearchs({
        title: this.searchKey
      })
        .then(res => {
          this.isLoading = false;
          //console.log('📌 模型数据接口返回成功，状态码：', res.status);

          if (res.status === 200) {
            // 存储全部数据并映射格式
            this.allModels = res.obj.map(item => this.mapApiDataToLocal(item));

            // 测试专用：手动追加数据，确保有足够滚动空间（新增）
            // if (this.allModels.length < 30) {
            //   this.allModels = [...this.allModels, ...this.allModels, ...this.allModels];
            //   //console.log('📌 测试数据已追加，当前总数据量：', this.allModels.length);
            // }

            this.totalCount = this.allModels.length;
            //console.log('📌 模型数据格式化完成，总数量：', this.totalCount);

            // 初始化显示第1批数据
            this.displayedModels = this.getBatchData(1);
            //console.log('📌 初始化显示第1批数据，数量：', this.displayedModels.length);

            // 判断是否有更多数据可显示（修复：使用准确判断逻辑）
            this.hasMore = this.filteredAllModels.length > this.displayedModels.length;
            //console.log('📌 初始化后，是否还有更多数据：', this.hasMore);
          }
        })
        .catch(err => {
          this.isLoading = false;
          //console.error('❌ 加载全部模型失败:', err);
        });
    },

    // 获取指定批次的模型数据（按pageSize拆分）
    getBatchData(batchNum) {
      const startIndex = (batchNum - 1) * this.pageSize;
      const endIndex = batchNum * this.pageSize;
      // 返回当前批次数据（基于筛选后的全部模型）
      const batchData = this.filteredAllModels.slice(startIndex, endIndex);
      //console.log(`📌 获取第${batchNum}批数据，起始索引：${startIndex}，结束索引：${endIndex}，数据量：${batchData.length}`);
      return batchData;
    },

    // 滚动懒加载显示下一批数据（优化：增加详细日志）
    handleScrollLazyLoad() {
      //console.log('📜 滚动事件已触发，进入防抖处理');
      // 防抖：300ms内只触发一次
      clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = setTimeout(() => {
        // 条件：不在加载中 + 还有更多数据 + 滚动到页面底部附近
        const isValid = !this.isLoading && !this.isAppendLoading && this.hasMore;
        // console.log('🚦 懒加载条件校验：', {
        //   isLoading: this.isLoading,
        //   isAppendLoading: this.isAppendLoading,
        //   hasMore: this.hasMore,
        //   isValid: isValid
        // });

        if (isValid) {
          // 兼容所有浏览器的滚动参数获取（优化）
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
          const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
          const clientHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

          // 修复：降低触发阈值到50px，更容易触发
          const triggerThreshold = 50;
          const isReachBottom = scrollTop + clientHeight >= scrollHeight - triggerThreshold;

          // 打印详细滚动参数（新增）
          // console.log('📊 滚动参数详情：', {
          //   scrollTop, // 已滚动距离
          //   clientHeight, // 可视区域高度
          //   scrollHeight, // 页面总高度
          //   triggerThreshold, // 触发阈值
          //   isReachBottom // 是否达到触发条件
          // });

          if (isReachBottom) {
            //console.log('✅ 满足懒加载触发条件，开始追加下一批数据');
            this.appendNextBatch();
          }
        }
      }, 300);
    },

    // 追加显示下一批数据（优化：增加防重复触发和日志）
    appendNextBatch() {
      // 新增：防重复触发保护
      if (this.isAppendLoading || !this.hasMore) {
        //console.log('🚫 重复触发追加加载，已拦截');
        return;
      }

      //console.log(`📥 开始追加第${this.currentBatch + 1}批数据`);
      this.isAppendLoading = true;
      this.currentBatch++;

      // 模拟加载延迟（可选，提升用户体验）
      setTimeout(() => {
        const nextBatchData = this.getBatchData(this.currentBatch);
        // 追加到当前显示列表
        this.displayedModels = [...this.displayedModels, ...nextBatchData];

        // 判断是否还有更多数据（当前显示条数 >= 筛选后总条数 → 无更多）
        this.hasMore = this.displayedModels.length < this.filteredAllModels.length;
        this.isAppendLoading = false;

        // console.log('📌 追加加载完成：', {
        //   当前批次: this.currentBatch,
        //   新增数据量: nextBatchData.length,
        //   总显示数据量: this.displayedModels.length,
        //   筛选后总数据量: this.filteredAllModels.length,
        //   是否还有更多: this.hasMore
        // });
      }, 500); // 延迟500ms，避免滚动过快导致显示突兀
    },

    // 模型详情跳转
    goToDetail(modelId) {
      // const url = this.$router.resolve({ path: `detail/${modelId}` }).href;
      // window.open(url, '_blank');
      this.$router.push({ path: `detail/?id=${modelId}` });
    },

    // 查看模型详情
    handleModelDetail(modelId) {
      console.log("查看模型详情，ID：", modelId);
    }
  }
};
</script>

<style lang="less" scoped>
@base-deep: #0f1118; // 页面基础底色
@base-darker: #080a12; // 卡片/区块背景色
@primary: #0ea5e9; // 主色调：科技蓝
@primary-light: #38bdf8; // 亮蓝色（hover/强调）
@accent: #7c3aed; // 辅助色：深紫色
@text-primary: #fff; // 主要文本色
@text-secondary: #94a3b8; // 次要文本色
@border-color: rgba(148, 163, 184, 0.1); // 边框色
@skeleton-bg: rgba(30, 41, 59, 0.5); // 骨架屏背景色

/* 全局容器样式 - 核心修改：移除固定宽度，改为自适应 + 强制允许滚动 */
.app-container {
  background-color: @base-deep;
  color: @text-primary;
  min-height: 100vh;
  width: 100%;
  /* 改为100%宽度，适配所有屏幕 */
  max-width: 1920px;
  /* 最大宽度限制，大屏不拉伸 */
  margin: 0 auto;
  padding: 0 15px;
  /* 左右内边距，防止内容贴边 */
  overflow-x: hidden;
  /* 禁止横向滚动 */
  overflow: visible !important;
  /* 新增：强制允许纵向滚动，不阻塞页面 */
  box-sizing: border-box;
  /* 内边距计入宽度 */
}

// 新增：穿透scoped，强制设置html/body允许滚动（关键修复）
:deep(html),
:deep(body) {
  overflow: auto !important;
  height: auto !important;
  margin: 0;
  padding: 0;
}

// 通用容器类 - 响应式适配
.container {
  width: 100%;
  max-width: 1785px;
  /* 最大宽度 */
  margin: 0 auto;
  padding: 0 15px;
  /* 响应式内边距 */
  box-sizing: border-box;
}

// 搜索结果头部样式 - 响应式优化
.search-results-header {
  background-color: @base-darker;
  padding: clamp(1rem, 2vw, 1.5rem) 0;
  /* 响应式内边距 */
  margin: 0 0 clamp(1rem, 2vw, 2rem);
  box-shadow: 0 2px 15px rgba(14, 165, 233, 0.1);
  width: 100%;

  h1 {
    font-size: clamp(1rem, 2vw, 1.5rem);
    /* 响应式字体大小 */
    color: @text-primary;
    width: 100%;
    margin: 0 auto;
    padding: 0 15px;
    /* 移除固定2rem内边距，改为15px */
    box-sizing: border-box;
    word-wrap: break-word;
    /* 长文本换行 */
    overflow-wrap: break-word;
    line-height: 1.4;
    /* 优化行高，提升可读性 */
  }
}

/* 模型展示区样式 - 全面响应式优化 */
.models-section {
  width: 100%;
  /* 改为100%宽度 */
  margin: 0 auto clamp(2rem, 3vw, 3rem);
  box-sizing: border-box;
  overflow: visible !important;
  /* 新增：不阻塞滚动 */

  .models-grid {
    display: grid;
    /* 关键：响应式网格布局 - 最小220px，自动填充，间距自适应 */
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 100%/4, 280px), 1fr));
    gap: clamp(1rem, 2vw, 1.5rem);
    /* 响应式间距 */
    margin-bottom: clamp(1rem, 2vw, 2rem);
    width: 100%;
  }

  .model-card {
    background-color: @base-darker;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.3s;
    cursor: pointer;
    border: 1px solid @border-color;
    width: 100%;
    /* 卡片宽度100% */
    box-sizing: border-box;
  }

  .model-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(14, 165, 233, 0.15);
    border-color: rgba(14, 165, 233, 0.2);
  }

  .model-img-container {
    position: relative;
    width: 100%;
    /* 关键：使用宽高比替代固定高度，保持比例 */
    aspect-ratio: 4/3;
    overflow: hidden;
  }

  .model-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
    filter: brightness(0.7);
  }

  .model-card:hover .model-img {
    transform: scale(1.05);
    filter: brightness(0.9);
  }

  .model-tag {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: linear-gradient(135deg, @primary, @accent);
    color: #fff;
    font-size: clamp(0.7rem, 1vw, 0.8rem);
    padding: 0.2rem 0.5rem;
    border-radius: 2px;
    box-shadow: 0 2px 4px rgba(14, 165, 233, 0.3);
    white-space: nowrap;
    /* 标签不换行 */
  }

  // 官方认证标签
  .model-official-tag {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    background: rgba(245, 158, 11, 0.9);
    color: #fff;
    font-size: clamp(0.7rem, 1vw, 0.8rem);
    padding: 0.2rem 0.5rem;
    border-radius: 2px;
    white-space: nowrap;
    z-index: 10;
  }

  // 可开票标签
  .model-invoice-tag {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: linear-gradient(135deg, #e53935, #7c3aed);
    color: #fff;
    font-size: clamp(0.7rem, 1vw, 0.8rem);
    padding: 0.2rem 0.5rem;
    border-radius: 2px;
    white-space: nowrap;
    z-index: 10;
  }

  .model-info {
    padding: clamp(0.8rem, 1.5vw, 1rem);
    /* 响应式内边距 */
    box-sizing: border-box;
  }

  .model-name {
    font-size: clamp(0.9rem, 1.5vw, 1rem);
    color: @text-primary;
    margin-bottom: 0.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-wrap: break-word;
  }

  .model-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 0.5rem;
    border-top: 1px solid @border-color;
    flex-wrap: wrap;
    /* 小屏幕换行 */
    gap: 0.5rem;
    /* 换行时间距 */
  }

  .model-price {
    color: @primary-light;
    font-weight: 600;
    font-size: clamp(0.9rem, 1.5vw, 1rem);
  }

  .model-sales {
    color: @text-secondary;
    font-size: clamp(0.8rem, 1.2vw, 0.9rem);
  }

  // 无数据提示
  .no-data {
    text-align: center;
    padding: clamp(2rem, 4vw, 3rem) 0;
    color: @text-secondary;
    font-size: clamp(1rem, 2vw, 1.2rem);
    width: 100%;
  }

  // 加载中提示
  .loading {
    text-align: center;
    padding: clamp(1.5rem, 3vw, 2rem) 0;
    color: @primary-light;
    font-size: clamp(0.9rem, 1.8vw, 1rem);
    width: 100%;
  }

  // 无更多数据提示
  .no-more {
    text-align: center;
    padding: clamp(0.8rem, 1.5vw, 1rem) 0;
    color: @text-secondary;
    font-size: clamp(0.8rem, 1.5vw, 0.9rem);
    width: 100%;
  }

  // 首次加载骨架屏 - 响应式优化
  .skeleton-container {
    display: grid;
    /* 与模型网格保持一致的响应式布局 */
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 100%/4, 280px), 1fr));
    gap: clamp(1rem, 2vw, 1.5rem);
    margin-bottom: clamp(1rem, 2vw, 2rem);
    width: 100%;
  }

  .skeleton-card {
    background-color: @base-darker;
    border-radius: 8px;
    overflow: hidden;
    /* 骨架屏高度使用宽高比保持一致 */
    aspect-ratio: 4/3;
    height: auto;
    /* 移除固定320px高度 */
    animation: skeleton-loading 1.5s infinite alternate;
    width: 100%;
  }

  @keyframes skeleton-loading {
    0% {
      background-color: @skeleton-bg;
    }

    100% {
      background-color: rgba(45, 65, 94, 0.5);
    }
  }
}

/* 媒体查询精细化适配 - 针对不同屏幕尺寸优化 */
// 平板横向
@media (max-width: 1200px) {
  .models-grid {
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 33.33%, 280px), 1fr));
  }
}

// 平板竖向
@media (max-width: 992px) {
  .models-grid {
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 50%, 280px), 1fr));
  }
}

// 手机大屏
@media (max-width: 768px) {
  .models-grid {
    grid-template-columns: repeat(auto-fill, minmax(clamp(200px, 50%, 280px), 1fr));
    gap: 1rem;
  }

  .model-meta {
    flex-direction: column;
    align-items: flex-start;
  }
}

// 手机小屏
@media (max-width: 576px) {
  .models-grid {
    grid-template-columns: 1fr;
    /* 单列显示 */
    gap: 0.8rem;
  }

  .search-results-header h1 {
    font-size: 1.2rem;
    line-height: 1.4;
  }
}

// 超小屏（400px以下）
@media (max-width: 400px) {
  .app-container {
    padding: 0 8px;
  }

  .container {
    padding: 0 8px;
  }

  .model-info {
    padding: 0.8rem;
  }
}
</style>